import { useState } from 'react'
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorState, Loading, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useMyPermissions, type TagDef } from '@/hooks/use-settings'
import { ZaloLabelImportButton } from '@/pages/conversations/zalo-label-import-dialog'

const DEFAULT_COLOR = '#3b82f6'
const PRESET_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#ef4444']

function TagPreview({ name, color }: { name: string; color: string }) {
  const { t } = useTranslation('settings')
  return (
    <Badge
      className="border-transparent"
      style={{ backgroundColor: `${color}20`, color }}
    >
      {name || t('settings:tags.preview')}
    </Badge>
  )
}

export function TagsTab() {
  const { data: tags, isLoading, isError } = useTags()
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const deleteTag = useDeleteTag()
  const { t } = useTranslation('settings')
  // Quản lý / Nhân viên mặc định chỉ có `tags.view` → không hiện Thêm / Sửa / Xoá (backend đòi tags.create/update/delete).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('tags.create')
  const canUpdate = !!perms?.has('tags.update')
  const canDelete = !!perms?.has('tags.delete')

  const [editTarget, setEditTarget] = useState<TagDef | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<TagDef | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<{ name: string; color: string }>({ name: '', color: DEFAULT_COLOR })

  function openCreate() {
    setEditTarget(null)
    setForm({ name: '', color: DEFAULT_COLOR })
    setDialogOpen(true)
  }

  function openEdit(t: TagDef) {
    setEditTarget(t)
    setForm({ name: t.name, color: t.color })
    setDialogOpen(true)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const name = form.name.trim()
    if (!name) {
      toast.error(t('settings:tags.nameRequired'))
      return
    }
    try {
      if (editTarget) {
        await updateTag.mutateAsync({ id: editTarget.id, name, color: form.color })
        toast.success(t('settings:tags.updated'))
      } else {
        await createTag.mutateAsync({ name, color: form.color })
        toast.success(t('settings:tags.created'))
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onDelete() {
    if (!deleteTarget) return
    try {
      await deleteTag.mutateAsync(deleteTarget.id)
      toast.success(t('settings:tags.deleted'))
      setDeleteTarget(null)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const saving = createTag.isPending || updateTag.isPending

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {tags ? t('settings:tags.count', { count: tags.length }) : t('settings:tags.listTitle')}
          </p>
          {canCreate && (
            <Button onClick={openCreate}>
              <Plus /> {t('settings:tags.add')}
            </Button>
          )}
        </div>

        {isLoading ? (
          <Loading label={t('settings:tags.loading')} />
        ) : isError ? (
          <ErrorState />
        ) : !tags?.length ? (
          <EmptyState title={t('settings:tags.empty')} description={t('settings:tags.emptyHint')} />
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="group flex items-center gap-2 rounded-lg border bg-card px-3 py-2"
              >
                <TagPreview name={tag.name} color={tag.color} />
                {(canUpdate || canDelete) && (
                  <div className="flex items-center gap-0.5">
                    {canUpdate && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(tag)} aria-label={t('common:actions.edit')}>
                        <Pencil className="!size-3.5" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => setDeleteTarget(tag)}
                        aria-label={t('common:actions.delete')}
                      >
                        <Trash2 className="!size-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Dialog thêm/sửa */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editTarget ? t('settings:tags.editTitle') : t('settings:tags.addTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="tag-name">{t('settings:tags.nameLabel')}</Label>
              <Input
                id="tag-name"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder={t('settings:tags.namePlaceholder')}
                maxLength={50}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tag-color">{t('settings:tags.colorLabel')}</Label>
              <div className="flex items-center gap-3">
                <input
                  id="tag-color"
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((s) => ({ ...s, color: e.target.value }))}
                  className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
                />
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, color: c }))}
                      className="h-6 w-6 rounded-full border"
                      style={{ backgroundColor: c }}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('settings:tags.previewLabel')}</Label>
              <div>
                <TagPreview name={form.name} color={form.color} />
              </div>
            </div>
            {/* Công cụ: kéo nhãn đã gắn ở Zalo PC về (chỉ khi thêm nhãn mới). */}
            {!editTarget && <ZaloLabelImportButton />}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="animate-spin" />}
                {editTarget ? t('common:actions.save') : t('settings:tags.createAction')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog xóa */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('settings:tags.deleteTitle')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="settings:tags.deleteDescription"
                values={{ name: deleteTarget?.name }}
                components={{ b: <span className="font-medium text-foreground" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={deleteTag.isPending}>
              {deleteTag.isPending && <Loader2 className="animate-spin" />}
              {t('common:actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
